import { EventDispatcher, Component } from '../core';

export const DEFAULT_OPTIONS = {
  toggler: '[data-pl-dropdown-role="toggler"]',
  target: '[data-pl-dropdown-role="target"]',
};

interface DropdownEventMap {
  beforeHide: Dropdown;
  afterHide: Dropdown;
  beforeShow: Dropdown;
  afterShow: Dropdown;
}

export class Dropdown implements Component<DropdownEventMap> {
  private dropdown: HTMLElement;
  private toggler: HTMLElement;
  private target: HTMLElement;
  private shownClass: string;
  private dispatcher: EventDispatcher<DropdownEventMap>;
  private currentDocumentListener?: (ev: MouseEvent) => void = null;

  constructor(
    dropdown: HTMLElement,
    options?: { toggler?: HTMLElement; target?: HTMLElement }
  ) {
    this.dropdown = dropdown;
    options = options || {};
    this.toggler = this.__resolveElement(
      options.toggler,
      this.dropdown,
      DEFAULT_OPTIONS.toggler
    );
    this.target = this.__resolveElement(
      options.target,
      this.dropdown,
      DEFAULT_OPTIONS.target
    );
    this.shownClass = 'is-shown';
    this.dispatcher = new EventDispatcher();
    this.__init();
  }

  addEventListener<K extends keyof DropdownEventMap>(
    type: K,
    listener: (event: DropdownEventMap[K]) => void
  ): void {
    this.dispatcher.addEventListener(type, listener);
  }

  removeEventListener<K extends keyof DropdownEventMap>(
    type: K,
    listener: (event: DropdownEventMap[K]) => void
  ): void {
    this.dispatcher.removeEventListener(type, listener);
  }

  destroy(): void {
    this.dropdown = null;
    this.toggler = null;
    this.target = null;
    this.currentDocumentListener = null;
    this.dispatcher.clearAll();
  }

  isOpen(): boolean {
    return (
      this.target &&
      this.target.classList &&
      this.target.classList.contains(this.shownClass)
    );
  }

  closeDropdown(): void {
    this.dispatcher.dispatchEvent('beforeHide', this);
    this.target.classList.remove(this.shownClass);
    this.dispatcher.dispatchEvent('afterHide', this);
    this.toggler.setAttribute('aria-expanded', 'false');
    this.__cleanDocListner();
  }

  openDropdown(): void {
    this.dispatcher.dispatchEvent('beforeShow', this);
    this.target.classList.add(this.shownClass);
    this.dispatcher.dispatchEvent('afterShow', this);
    this.toggler.setAttribute('aria-expanded', 'true');
    this.__attachDocListener();
  }

  __resolveElement(
    maybeDomElement: unknown,
    parent: HTMLElement,
    defaultSelector: string
  ): HTMLElement {
    if (this.__isObject(maybeDomElement)) {
      return maybeDomElement as HTMLElement;
    } else {
      return parent.querySelector(defaultSelector);
    }
  }

  __isObject(value: unknown): boolean {
    return typeof value === 'object' && value !== null;
  }

  __toggleDropdown(): void {
    if (this.isOpen()) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  __attachDocListener(): void {
    if (this.isOpen()) {
      if (this.currentDocumentListener) {
        this.__cleanDocListner();
      }
      this.currentDocumentListener = (event: MouseEvent) => {
        if (
          event.target &&
          event.target !== this.toggler &&
          (event.target as Element).parentElement !== this.toggler
        ) {
          this.closeDropdown();
        }
      };
      window.document.addEventListener('click', this.currentDocumentListener);
    }
  }

  __cleanDocListner(): void {
    document.removeEventListener('click', this.currentDocumentListener);
    this.currentDocumentListener = null;
  }

  __init(): void {
    this.toggler.addEventListener('click', this.__toggleDropdown.bind(this));
  }
}
